import { useEffect, useState } from 'react';
import { Collapse, Row, Col, Select, Typography, Affix, Button, Tooltip } from 'antd';
import { UserAddOutlined } from '@ant-design/icons';
import { useAuthZ } from '../../authZ/useAuthZ';
import { useUserSettingsLogic } from '../useUserSettingsLogic';
import UsersTable from './UsersTable';
import CreateUserModal from './CreateUserModal';
import { useI18nLocalization } from '../../i18nLocalization/useI18nLocalization';

const Users = () => {
	const authZ = useAuthZ();
	const userSettingsLogic = useUserSettingsLogic();

	//-- Used for translations of text
	const { t } = useI18nLocalization();

	//-- Data source
	const [users, setUsers] = useState([]);

	//-- Filters
	const [userOptions, setUserOptions] = useState([]);
	const [userFilter, setUserFilter] = useState([]);
	const [roleOptions, setRoleOptions] = useState([]);
	const [roleFilter, setRoleFilter] = useState([]);

	//-- List after filters applied
	const [filteredUsers, setFilteredUsers] = useState([]);

	const [createUserModalIsVisible, setCreateUserModalIsVisible] = useState(false);

	//-- When the list of users changes, perform updates
	useEffect(() => {
		if (users.length === 0) {
			getUsers();
		} else {
			createUserFilterOptions();
			createRoleFilterOptions();
			handleFilter();
		}
	}, [JSON.stringify(users)]);

	//-- Trigger a filter event whenever a filter changes
	useEffect(() => {
		handleFilter();
	}, [JSON.stringify(userFilter), JSON.stringify(roleFilter)]);

	//-- Get users from the API
	const getUsers = async () => {
		const usrs = await userSettingsLogic.getUsers();
		setUsers(usrs);
	};

	//-- Create the user filter options from the available users
	const createUserFilterOptions = () => {
		const uOptions = _.map(
			_.orderBy(users, (user) => user.profile.lastName, ['asc']),
			(u) => {
				const uOpt = {
					value: u.profile.emailAddress,
					label: `${u.profile.lastName}, ${u.profile.firstName}`
				};

				return uOpt;
			}
		);

		setUserOptions(uOptions);
	};

	//-- Create the role filter options from the available users
	const createRoleFilterOptions = () => {
		const roles = [
			...new Set(
				users
					.map((u) => u.roles)
					.flat()
					.map((r) => r.role)
			)
		];
		const rOptions = _.map(
			_.orderBy(roles, (r) => r, ['asc']),
			(r) => {
				const rOpt = {
					value: r,
					label: r
				};

				return rOpt;
			}
		);

		setRoleOptions(rOptions);
	};

	const handleFilter = () => {
		const filterdList = users.filter((u) => {
			const included =
				//-- Users
				(userFilter.length === 0 || userFilter.some((uf) => uf.match(new RegExp(u.profile.emailAddress, 'i')))) &&
				//-- Roles
				(roleFilter.length == 0 || roleFilter.some((rf) => u.roles.some((r) => r.role.match(new RegExp(rf, 'i')))));

			return included;
		});

		setFilteredUsers(filterdList);
	};

	return (
		<>
			<Collapse bordered={false} defaultActiveKey='filters'>
				<Collapse.Panel
					header={
						<Typography.Title level={5} style={{ display: 'inline-block' }}>
							{t('Filters')}
						</Typography.Title>
					}
					key='filters'
					extra={<i className='las la-filter la-lg primary-color' />}
					showArrow={false}
				>
					<Row gutter={[16, 16]}>
						<Col flex='auto'>
							<Typography.Title level={5}>{t('Users')}</Typography.Title>
							<Select
								showSearch
								mode='multiple'
								allowClear
								placeholder={t('Filter by user')}
								options={userOptions}
								filterOption={(input, option) => {
									return (
										option.value.toLowerCase().indexOf(input.toLowerCase()) >= 0 ||
										option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
									);
								}}
								onSelect={(value) => setUserFilter(userFilter.concat([value]))}
								onDeselect={(value) => setUserFilter(userFilter.filter((uf) => uf !== value))}
								style={{ width: '100%' }}
							/>
						</Col>
						<Col flex='auto'>
							<Typography.Title level={5}>{t('Roles')}</Typography.Title>
							<Select
								showSearch
								mode='multiple'
								allowClear
								placeholder={t('Filter by role')}
								options={roleOptions}
								filterOption={(input, option) => {
									return (
										option.value.toLowerCase().indexOf(input.toLowerCase()) >= 0 ||
										option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
									);
								}}
								onSelect={(value) => setRoleFilter(roleFilter.concat([value]))}
								onDeselect={(value) => setRoleFilter(roleFilter.filter((uf) => uf !== value))}
								style={{ width: '100%' }}
							/>
						</Col>
					</Row>
				</Collapse.Panel>
			</Collapse>
			<UsersTable className='margin-top-10-px' users={filteredUsers} refreshUserList={getUsers} />
			{authZ.userIsEntDevAdmin() && (
				<>
					<Affix style={{ position: 'absolute', bottom: '7%', right: '2%' }}>
						<Tooltip title={t('Create a new user for this profile')} placement='left'>
							<Button
								type='primary'
								shape='circle'
								size={'large'}
								icon={<UserAddOutlined />}
								style={{ boxShadow: '2px 2px 5px grey' }}
								onClick={() => {
									setCreateUserModalIsVisible(true);
								}}
							/>
						</Tooltip>
					</Affix>
					<CreateUserModal
						isVisible={createUserModalIsVisible}
						onSaveCallback={() => {
							getUsers();
							setCreateUserModalIsVisible(false);
						}}
						onCloseCallback={() => {
							setCreateUserModalIsVisible(false);
						}}
					/>
				</>
			)}
		</>
	);
};

export default Users;
